"use strict";

/**
 * WEB-4823: "Sale Applications Email" section of one law firm card on DMS → Project → Law Firms.
 *
 * One instance per card (`ng-controller` inside the page's `ng-repeat`), so each firm keeps its own
 * state. It edits the law firm's report code 16 rule (a developer_email_notifications row, also listed
 * in DMS → Settings → Email Notifications) through:
 *   GET  sec/developer/law-firms/sale-applications-notification?property_id=&law_firm_id=
 *   POST sec/developer/law-firms/sale-applications-notification
 *
 * @remarks Mirrors the agency "Commission Claimable Email" section (property/agency.js, WEB-4489):
 *          only changed fields are posted, the rule id is sent when one is loaded, a new rule starts
 *          OFF (daily, the server's default for code 16), 412001 messages show under the fields and
 *          412003 reloads the section. Kept out of DeveloperLawfirmController (index.js) on purpose,
 *          so it does not touch the page controller's code.
 */
class DeveloperLawfirmSaleApplicationsEmailController {

    constructor(SSHttpService, SSAlertService, SSUserService, OTPService, SSEmailNotificationScheduleService) {
        const self = this;

        self.SSHttpService = SSHttpService;
        self.SSAlertService = SSAlertService;
        self.SSUserService = SSUserService;
        self.OTPService = OTPService;
        self.ScheduleService = SSEmailNotificationScheduleService;

        self.data = SHOWSUITE.data;

        // Fortnightly is allowed for report code 16
        self.frequencyOptions = self.ScheduleService.getFrequencyOptions(true);

        self.lawfirmProject = null;
        self.allLawfirms = [];
        self.loadSeq = 0;
        self.reset();
    }

    /**
     * Bind the card and load its rule(s). Called from the card's `ng-init`.
     *
     * @param {Object} lawfirmProject A law_firm_projects row of the page (id, law_firm_id, lawyers[]).
     * @param {Array<Object>} allLawfirms The page's law firm list (sec/developer/law-firms: id, name, email).
     * @returns {void}
     */
    init(lawfirmProject, allLawfirms) {
        const self = this;

        self.lawfirmProject = lawfirmProject || null;
        self.allLawfirms = allLawfirms || [];
        self.load();
    }

    /**
     * Empty section state.
     *
     * @returns {void}
     */
    reset() {
        const self = this;

        self.email = {
            loading: false,
            loaded: false,
            loadFailed: false,
            available: false,
            rules: [],
            selectedRuleId: null,
            form: {},
            startDate: null,
            errors: {},
        };
    }

    /**
     * GET sec/developer/law-firms/sale-applications-notification is guarded by DMS_LAW_FIRM_PROJ_GET on the project.
     *
     * @returns {boolean}
     */
    canView() {
        const self = this;

        return !!(self.data && self.data.property)
            && self.SSUserService.hasPermissionForProperty('DMS_LAW_FIRM_PROJ_GET', self.data.property.id);
    }

    /**
     * POST is guarded by DMS_LAW_FIRM_PROJ_UPDATE on the project (+ a 2FA session).
     *
     * @returns {boolean}
     */
    canEdit() {
        const self = this;

        return !!(self.data && self.data.property)
            && self.SSUserService.hasPermissionForProperty('DMS_LAW_FIRM_PROJ_UPDATE', self.data.property.id);
    }

    /**
     * Whether the section is shown at all.
     *
     * @returns {boolean} True when the developer has report R111 (`available`), or when the settings
     *                    could not be loaded (so that failure is visible).
     */
    isVisible() {
        const self = this;

        return self.canView() && (self.email.available || self.email.loadFailed);
    }

    /**
     * GET the sale applications email rule(s) of this law firm on this project.
     *
     * @returns {void}
     *
     * @remarks Usually 0 or 1 rule; a rule set up in DMS → Settings may cover several projects.
     *          `available` false (no report R111) hides the section. A 403 / 404 (no permission, firm
     *          no longer assigned, or an API without the endpoint) hides it too; any other failure shows
     *          "Unable to load". A reply that is not for the latest request is ignored.
     */
    load() {
        const self = this;

        self.reset();
        let lawfirmProject = self.lawfirmProject;
        if (!lawfirmProject || !lawfirmProject.id || !lawfirmProject.law_firm_id || !self.canView()) {
            return;
        }

        let seq = ++self.loadSeq;
        let params = {
            property_id: self.data.property.id,
            law_firm_id: lawfirmProject.law_firm_id,
        };

        self.email.loading = true;
        self.SSHttpService.getAPIRequest('sec/developer/law-firms/sale-applications-notification', params).then(function (response) {
            if (seq !== self.loadSeq) {
                return;
            }
            self.email.loading = false;

            if (response instanceof Error) {
                // Non-blocking: the rest of the card still works
                console.warn('WEB-4823: Unable to load the sale applications email.', response);
                self.email.loadFailed = !(response.code == 403 || response.code == 404);
                return;
            }

            let data = response.data || {};
            self.email.available = !!data.available;
            if (!self.email.available) {
                return;
            }

            self.email.loaded = true;
            self.email.rules = data.sale_applications_notifications || [];
            self.selectRule(self.email.rules.length ? self.email.rules[0].id : null);
        });
    }

    /**
     * Show one rule in the form (or the defaults for a new one).
     *
     * @param {number|null} ruleId
     * @returns {void}
     *
     * @remarks A new rule's form starts switched OFF and daily, with the recipients pre-filled from the
     *          law firm's email and its lawyers' emails (editable). Saving with the switch off stores a
     *          rule that is not sent.
     */
    selectRule(ruleId) {
        const self = this;
        const S = self.ScheduleService;

        let rule = self.findRule(ruleId);

        self.email.selectedRuleId = rule ? rule.id : null;
        self.email.errors = {};
        self.email.form = rule ? self.ruleToForm(rule) : {
            is_enabled: 0,
            emails: self.getDefaultRecipients().join(', '),
            frequency: S.FREQUENCY_DAILY,
            schedule_day: null,
        };
        self.email.startDate = rule ? S.toDate(rule.schedule_start_date) : null;
    }

    /**
     * @param {number|null} ruleId
     * @returns {Object|null} The loaded rule with that id.
     */
    findRule(ruleId) {
        const self = this;

        if (!ruleId || !self.email) {
            return null;
        }

        return self.email.rules.find(x => x.id == ruleId) || null;
    }

    /**
     * Suggested recipients for a new rule: the law firm's email, then its lawyers on this project.
     *
     * @returns {string[]} Valid, de-duplicated addresses.
     */
    getDefaultRecipients() {
        const self = this;
        const S = self.ScheduleService;

        let lawfirmProject = self.lawfirmProject || {};
        let lawFirm = (self.allLawfirms || []).find(x => x.id == lawfirmProject.law_firm_id);
        let candidates = [lawFirm && lawFirm.email];
        (lawfirmProject.lawyers || []).forEach(function (lawyer) {
            candidates.push(lawyer.user_email);
        });

        return S.splitEmails(candidates.filter(email => !!email).join(','))
            .filter(email => !S.validateEmails(email));
    }

    /**
     * A rule's editable values in form shape (numbers for the selects, 1/0 for the switch).
     *
     * @param {Object} rule
     * @returns {{is_enabled: number, emails: string, frequency: (number|null), schedule_day: (number|null)}}
     */
    ruleToForm(rule) {
        const self = this;
        const S = self.ScheduleService;

        return {
            is_enabled: (rule.is_enabled === false || rule.is_enabled === 0 || rule.is_enabled === '0') ? 0 : 1,
            emails: rule.emails || '',
            frequency: S.toNumberOrNull(rule.frequency),
            schedule_day: S.toNumberOrNull(rule.schedule_day),
        };
    }

    /**
     * Label for the rule picker shown when 2+ rules cover this law firm on this project.
     *
     * @param {Object} rule
     * @returns {string} e.g. "Weekly on Tuesday — a@firm.com +1"
     */
    getRuleLabel(rule) {
        const self = this;

        let label = self.ScheduleService.describeSchedule(rule);
        let recipients = self.ScheduleService.splitEmails(rule.emails);
        if (recipients.length) {
            label += ' — ' + recipients[0] + (recipients.length > 1 ? ' +' + (recipients.length - 1) : '');
        }

        return label;
    }

    /**
     * How many OTHER projects the selected rule also covers.
     *
     * @returns {number}
     */
    getSharedCount() {
        const self = this;

        let rule = self.findRule(self.email && self.email.selectedRuleId);
        if (!rule || !rule.property_ids) {
            return 0;
        }
        let propertyId = String(self.data.property.id);

        return String(rule.property_ids).split(',')
            .map(id => id.trim())
            .filter((id, index, ids) => id && id !== propertyId && ids.indexOf(id) === index)
            .length;
    }

    /**
     * @returns {string} "Last sent" of the selected rule, or '' when never sent / no rule.
     */
    getLastSent() {
        const self = this;

        let rule = self.findRule(self.email && self.email.selectedRuleId);

        return rule && rule.last_sent_at ? self.ScheduleService.formatDisplayDateTime(rule.last_sent_at) : '';
    }

    /**
     * Frequency changed — a weekday and a day of the month are not interchangeable.
     *
     * @returns {void}
     */
    onFrequencyChange() {
        const self = this;

        self.email.form.schedule_day = null;
        delete self.email.errors.schedule_day;
        delete self.email.errors.schedule_start_date;
    }

    /**
     * The fields to POST — every field for a new rule, only the changed ones otherwise (the server
     * keeps the saved value of an omitted field).
     *
     * @returns {Object} Without property_id / law_firm_id / developer_email_notification_id.
     *
     * @remarks For a fortnightly rule schedule_day is never compared or sent: the server derives it
     *          from the start date, so it would otherwise always look changed.
     */
    buildChanges() {
        const self = this;
        const S = self.ScheduleService;

        let form = self.email.form;
        let desired = angular.extend({
            emails: S.splitEmails(form.emails).join(','),
            is_enabled: form.is_enabled ? 1 : 0,
        }, S.buildSchedule(form, self.email.startDate));

        let rule = self.findRule(self.email.selectedRuleId);
        if (desired.frequency === S.FREQUENCY_FORTNIGHTLY) {
            delete desired.schedule_day;
        }
        if (!rule) {
            return desired;
        }

        let saved = {
            emails: S.splitEmails(rule.emails).join(','),
            is_enabled: self.ruleToForm(rule).is_enabled,
            frequency: S.toNumberOrNull(rule.frequency),
            schedule_day: S.toNumberOrNull(rule.schedule_day),
            schedule_start_date: rule.schedule_start_date ? String(rule.schedule_start_date).substr(0, 10) : null,
        };

        let changes = {};
        Object.keys(desired).forEach(function (field) {
            if (desired[field] !== saved[field]) {
                changes[field] = desired[field];
            }
        });

        return changes;
    }

    /**
     * SAVE (create / update / switch on-off) this law firm's sale applications email via
     * POST sec/developer/law-firms/sale-applications-notification, after a 2FA session check.
     *
     * @returns {void}
     *
     * @remarks Saved on its own button: it is a developer_email_notifications row, not part of the
     *          law firm assignment, and may cover several projects. Server field messages (412001)
     *          show under the fields; 412003 (rule gone / firm no longer assigned) reloads the section.
     */
    save() {
        const self = this;
        const S = self.ScheduleService;

        if (!self.canEdit() || !self.lawfirmProject || !self.lawfirmProject.id || !self.email.loaded) {
            return;
        }

        self.OTPService.getVerified(
            self.OTPService.TYPE_SESSION_2FA,
            function (data) {
                let errors = S.validateSchedule(self.email.form, self.email.startDate, true);
                let emailError = S.validateEmails(self.email.form.emails);
                if (emailError) {
                    errors.emails = emailError;
                }
                self.email.errors = errors;
                if (Object.keys(errors).length) {
                    self.SSAlertService.warning('Missing Params!', 'Please correct the sale applications email settings.');
                    return;
                }

                let changes = self.buildChanges();
                if (!Object.keys(changes).length) {
                    self.SSAlertService.info('No changes', 'The sale applications email settings are unchanged.');
                    return;
                }

                let params = angular.extend({
                    property_id: self.data.property.id,
                    law_firm_id: self.lawfirmProject.law_firm_id,
                }, changes);
                if (self.email.selectedRuleId) {
                    params.developer_email_notification_id = self.email.selectedRuleId;
                }

                loading(true);
                self.SSHttpService.postAPIRequest('sec/developer/law-firms/sale-applications-notification', params).then(function (response) {
                    loading(false);

                    if (response instanceof Error) {
                        if (response.sub_code == 412003) {
                            self.SSAlertService.danger('Error!', 'This email setting no longer exists. It has been reloaded.');
                            self.load();
                            return;
                        }
                        self.email.errors = S.parseFieldErrors(response);
                        if (Object.keys(self.email.errors).length) {
                            self.SSAlertService.warning('Not saved', 'Please correct the highlighted sale applications email fields.');
                        } else {
                            self.SSAlertService.parseAndDisplayError(response);
                        }
                        return;
                    }

                    let saved = response.data && response.data.sale_applications_notification;
                    if (saved) {
                        let index = self.email.rules.findIndex(x => x.id == saved.id);
                        if (index >= 0) {
                            self.email.rules.splice(index, 1, saved);
                        } else {
                            self.email.rules.push(saved);
                        }
                        self.selectRule(saved.id);
                    }
                    self.SSAlertService.success('Sale applications email saved successfully.');
                });
            }
        );
    }
}

DeveloperLawfirmSaleApplicationsEmailController.$inject = ['SSHttpService', 'SSAlertService', 'SSUserService', 'OTPService', 'SSEmailNotificationScheduleService'];
app.controller('DeveloperLawfirmSaleApplicationsEmailController', DeveloperLawfirmSaleApplicationsEmailController);
